import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page3FaqJsonLd } from '@/lib/jsonld';
import { getFaqQuestions } from '@/components/web-viewer-for-instagram/FAQContent/FaqData';
import { AllSupportLanguages } from '@/shared/language';
import { getRevalidateTime, SHOW_ISR_DEBUG_PANEL, SITE_HOST, ISR_ENV } from '@/shared/isr-config';
import ISRDebugPanel from '@/components/Common/ISRDebugPanel';

const WebViewerForInstagramContent = dynamic(() => import('@/components/web-viewer-for-instagram'), {
  ssr: true,
});

const PAGE_PATH = '/web-viewer-for-instagram';

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;

  const [localeRes, tdkRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(PAGE_PATH, locale),
  ]);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${SITE_HOST}/${locale}` : `https://${SITE_HOST}`;

  // 创建一个简单的翻译函数，用于在服务端获取FAQ数据
  const t = (key: string) => tempData[key] || key;

  // 获取FAQ问题数据用于faqJsonLd
  const faqQuestions = getFaqQuestions(t);

  const jsonLd = page3FaqJsonLd(
    {
      title: 'Web Viewer for Instagram',
      desc: tdkRes?.data?.description ?? tempData?.head_Viewer_Instagram_Meta_Desc ?? null,
      url: canonicalUrl(SITE_HOST, PAGE_PATH, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    { url: siteUrl },
    faqQuestions, // 正确传递FAQ问题数组
    {
      name: 'Instagram Post Viewer – View IG Posts, Stories and Profiles',
      url: 'https://www.dolphinradar.com/web-viewer-for-instagram',
      applicationCategory: 'Instagram Post Viewer',
      description: 'View and download posts, stories, and profiles from public accounts with ease.',
      featureList: [
        'Post Viewer',
        'Post Downloader',
        'Story Viewer',
        'Story Downloader',
        'Highlights Viewer',
        'Highlights Downloader',
      ],
    }
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(SITE_HOST, PAGE_PATH, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Viewer_Instagram_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Viewer_Instagram_Meta_Desc ?? null,
      jsonLd,
      // ISR 调试信息
      __isr_generated_at: Date.now(),
      __isr_revalidate: getRevalidateTime('web-viewer-for-instagram'),
      __isr_page_name: 'web-viewer-for-instagram',
      __isr_env: ISR_ENV,
    },
    revalidate: getRevalidateTime('web-viewer-for-instagram'),
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return useMemo(
    () => (
      <>
        <WebViewerForInstagramContent />
        {SHOW_ISR_DEBUG_PANEL && (
          <ISRDebugPanel
            pageName={props.__isr_page_name}
            generatedAt={props.__isr_generated_at}
            revalidate={props.__isr_revalidate}
            env={props.__isr_env}
          />
        )}
      </>
    ),
    [props]
  );
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
          {AllSupportLanguages.map((lang) => (
            <link
              key={lang.code}
              rel="alternate"
              hrefLang={lang.code}
              href={`https://www.dolphinradar.com/${lang.code}/web-viewer-for-instagram`}
            />
          ))}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
